/**
 * 高德地图插件 - 路线轮询 Hook
 *
 * 使用场景：订单详情地图，前端按固定间隔（30s/60s）拉取司机最新坐标，
 * 再调路径规划接口获取折线，更新到地图 Polyline 实例（setPath 而非重建）。
 *
 * 减重绘策略：
 * 1. enabled=false 或 points < 2 时不发起任何请求
 * 2. 设置 minMoveMeters 阈值：司机位移小于该值时跳过本轮规划
 * 3. 同一时间只允许一个请求在途（runningRef），防止并发规划
 * 4. intervalMs=0 时不开启定时器，仅响应 points 引用变化（地址列表切换场景）
 *
 * 用法示例（订单详情）：
 *   const { routeResult } = useAmapRoutePolling({
 *     enabled: !!mapInstance && hasPoints,
 *     points: [driverPoint, ...destPoints],
 *     intervalMs: 30_000,
 *     minMoveMeters: 50,
 *   })
 */
import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { DEFAULT_ROUTE_POLLING_INTERVAL } from './constants'
import { calcPointDistance, fetchRoutePlan } from './route-client'
import type { AmapLngLatPoint, RoutePathPlaceholder, UseAmapRoutePollingOptions } from './types'

const hasEnoughPoints = (points: AmapLngLatPoint[]) => {
  return points.length >= 2
}

export const useAmapRoutePolling = (options: UseAmapRoutePollingOptions) => {
  const { enabled, points, intervalMs = 0, minMoveMeters = 10 } = options

  const [routeResult, setRouteResult] = useState<RoutePathPlaceholder | null>(null)
  const pollingTimerRef = useRef<number | null>(null)
  /** 防止同时多个规划请求并发 */
  const runningRef = useRef(false)
  /** 上一次成功规划时的起始点，用于位移阈值比较 */
  const prevHeadPointRef = useRef<AmapLngLatPoint | null>(null)
  /** 使用 ref 同步最新 points，避免定时器闭包捕获旧值 */
  const pointsRef = useRef<AmapLngLatPoint[]>(points)

  const runRoutePlanning = useCallback(async () => {
    if (!enabled || runningRef.current || !hasEnoughPoints(pointsRef.current)) {
      return
    }

    const nextHeadPoint = pointsRef.current[0]
    const prevHeadPoint = prevHeadPointRef.current
    if (prevHeadPoint) {
      const movedDistance = calcPointDistance(prevHeadPoint, nextHeadPoint)
      if (movedDistance < minMoveMeters) {
        return
      }
    }

    runningRef.current = true
    try {
      const result = await fetchRoutePlan({
        points: pointsRef.current,
      })
      setRouteResult(result)
      prevHeadPointRef.current = nextHeadPoint
    } finally {
      runningRef.current = false
    }
  }, [enabled, minMoveMeters])

  /** points 序列化为 key，内容不变时不触发重新规划 */
  const pointsKey = useMemo(() => {
    return points
      .map((item) => `${item.lon}-${item.lat}`)
      .join('|')
  }, [points])

  useEffect(() => {
    pointsRef.current = points
    if (!enabled || !hasEnoughPoints(points)) {
      setRouteResult(null)
      prevHeadPointRef.current = null
      return
    }
    runRoutePlanning()
  }, [enabled, pointsKey, points, runRoutePlanning])

  /** 定时轮询，interval 最小取 DEFAULT_ROUTE_POLLING_INTERVAL（30s） */
  useEffect(() => {
    if (!enabled || intervalMs <= 0) {
      return undefined
    }

    const pollingInterval = Math.max(intervalMs, DEFAULT_ROUTE_POLLING_INTERVAL)
    pollingTimerRef.current = window.setInterval(() => {
      runRoutePlanning()
    }, pollingInterval)

    return () => {
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
    }
  }, [enabled, intervalMs, runRoutePlanning])

  return {
    /** 最新路径规划结果，null 表示尚未规划完成或已清空 */
    routeResult,
    /** 手动触发一次规划（可在外部轮询到新司机坐标后调用） */
    runRoutePlanning,
  }
}
